import { listSessionsByFolder } from '@/api/sessions';

/** The closed folder is read at most this many pages (50 rows each) for the My Day figure. */
export const RESOLVED_TODAY_MAX_PAGES = 5;
const PAGE_SIZE = 50;

export interface ResolvedToday {
  /** Conversations of mine that closed today, among the pages that were read. */
  count: number;
  /**
   * True when the read reached rows older than today (or the end of the folder): `count` is then the whole answer. False
   * when the page cap stopped it first: `count` is a LOWER BOUND and the screen says so ("12+").
   */
  complete: boolean;
}

const sameLocalDay = (iso: string | null | undefined, now: Date): boolean => {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
};

/**
 * "Resolved today" for My Day (ADR-033 D5/D6): the closed folder (`folder=all-closed`) lists every closed conversation the
 * viewer may see, newest ACTIVITY first, and a closed row keeps its owner. So the figure is the rows on it that are mine
 * (`ownerAgentId === me`) and closed today (`closedAt`, in the viewer's own day).
 *
 * <b>It is approximate, and the screen says so.</b> The folder is keyed on last activity, not on the close time, and it is
 * read a page at a time; the read stops at the first page whose oldest row had its last activity before today (a
 * conversation closed today has activity today), or at the end of the folder, or at the page cap. Only the first two make the
 * number complete. A failed page rejects: the caller shows "not available", never a smaller number passed off as the answer.
 */
export async function countMyResolvedToday(me: string, now: Date = new Date()): Promise<ResolvedToday> {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  let cursor: string | null = null;
  let count = 0;
  for (let page = 0; page < RESOLVED_TODAY_MAX_PAGES; page += 1) {
    const res = await listSessionsByFolder('all-closed', cursor, PAGE_SIZE);
    for (const s of res.items) {
      if (typeof s.ownerAgentId === 'string' && s.ownerAgentId === me && sameLocalDay(s.closedAt, now)) count += 1;
    }
    if (!res.hasMore || !res.nextCursor) return { count, complete: true };
    const oldest = res.items[res.items.length - 1];
    const oldestActivity = Date.parse(oldest?.lastActivityAt ?? oldest?.closedAt ?? oldest?.updatedAt ?? '');
    if (Number.isFinite(oldestActivity) && oldestActivity < startOfToday) return { count, complete: true };
    cursor = res.nextCursor;
  }
  return { count, complete: false };
}
